import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Button,
} from "@mui/material";
import { motion } from "framer-motion";
import SwanLogo from "../../assets/logos/swansolutions_logo.png"; // Consistent logo filename
import { useSiteContent } from "../../services/content";
import { mediaThumb } from "../../services/media";

export default function Districts() {
    const content = useSiteContent();
    const [districts, setDistricts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");

    useEffect(() => {
        // District partners come from the CRM Accounts table (Status = Customer),
        // via the figazuresql autoscaffold API, mapped to the page's shape.
        fetch("https://figazuresql.swancreate.com/api/crm/accounts?Status=Customer")
            .then((res) => res.json())
            .then((data) => {
                const list = (Array.isArray(data) ? data : [])
                    .filter((a) => a.District && ("" + a.District).trim())
                    .map((a) => ({
                        apiid: a.apiid,
                        DistrictName: a.District,
                        State: a.State || "",
                        Country: "USA",
                        IsActive: true,
                        DistrictLogoUrl: a.logoUrl || null,
                    }));
                setDistricts(list);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Unique states for filter dropdown
    const states = Array.from(
        new Set(
            districts.filter((d) => d.IsActive).map((d) => d.State).filter(Boolean)
        )
    ).sort();

    // Filter only active and matching
    const filtered = districts
        .filter((d) => d.IsActive)
        .filter((d) =>
            d.DistrictName.toLowerCase().includes(nameFilter.toLowerCase())
        )
        .filter((d) => !stateFilter || d.State === stateFilter);

    const defaultLogo = "/logos/fig_CROP.png";

    return (
        <Box sx={{ bgcolor: "#f7fafb" }}>
            {/* HERO: full-width, gradient, logo, white text */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 14 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "30vh", md: "36vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 8,
                }}
            >
                <Box
                    component="img"
                    src={SwanLogo}
                    alt="Swan Solutions"
                    sx={{
                        height: { xs: 70, md: 110 },
                        mb: 3,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.12))",
                    }}
                />
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 900,
                        mb: 2,
                        fontSize: { xs: "2rem", md: "3rem" },
                        letterSpacing: "-0.04em",
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    {content("districts.hero.title", "Our District Partners")}
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        maxWidth: 800,
                        mx: "auto",
                        mb: 3,
                        color: "rgba(255,255,255,0.97)",
                        fontWeight: 400,
                        fontSize: { xs: "1.1rem", md: "1.2rem" },
                        textShadow: "0 2px 12px rgba(40,120,140,0.08)",
                    }}
                >
                    {content("districts.hero.subtitle", "Empowering districts of all sizes to lead with technology. Our platform supports school systems nationwide in streamlining operations, simplifying compliance, and delivering better student outcomes.")}
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    href="/contact"
                    component={motion.a}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.96 }}
                    sx={{
                        mt: 2,
                        borderRadius: 99,
                        fontWeight: 700,
                        px: 4,
                        py: 1.4,
                        fontSize: { xs: "1rem", md: "1.09rem" },
                        background: "linear-gradient(90deg, #145c8c, #21dbb8)",
                        boxShadow: "0 3px 14px rgba(44,153,200,0.12)",
                    }}
                >
                    Contact Us
                </Button>
            </Box>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, mb: 3 }}>
                    District Customers
                </Typography>

                {/* Filters Section */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        mb: 4,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: { xs: "center", md: "flex-start" },
                    }}
                >
                    <TextField
                        label="Search by district"
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        sx={{ flex: 1, minWidth: 220, maxWidth: 330 }}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                            sx: { borderRadius: 2 }
                        }}
                    />
                    <FormControl sx={{ minWidth: 180 }}>
                        <InputLabel>Filter by state</InputLabel>
                        <Select
                            value={stateFilter}
                            label="Filter by state"
                            onChange={(e) => setStateFilter(e.target.value)}
                            sx={{ borderRadius: 2, bgcolor: "#fff" }}
                        >
                            <MenuItem value="">All States</MenuItem>
                            {states.map((st) => (
                                <MenuItem key={st} value={st}>
                                    {st}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {filtered.map((d) => (
                            <Grid key={d.apiid || d.DistrictName} item xs={12} sm={6} md={4} lg={3}>
                                <motion.div
                                    initial={{ scale: 0.94, opacity: 0 }}
                                    whileInView={{ scale: 1, opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ type: "spring", stiffness: 130, damping: 20 }}
                                    whileHover={{ scale: 1.025, boxShadow: "0 6px 24px #21dbb820" }}
                                >
                                    <Card
                                        sx={{
                                            height: "100%",
                                            borderRadius: 3,
                                            boxShadow: 5,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            transition: "box-shadow 0.2s, transform 0.15s",
                                            px: 0.5,
                                            py: 1.5,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={d.DistrictLogoUrl ? mediaThumb(d.DistrictLogoUrl, 240) : defaultLogo}
                                            alt={d.DistrictName}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = defaultLogo;
                                            }}
                                            sx={{
                                                height: 100,
                                                width: "100%",
                                                objectFit: "contain",
                                                p: 2,
                                                mb: 1,
                                                bgcolor: "#f6fafb",
                                                borderRadius: 2,
                                            }}
                                        />
                                        <CardContent sx={{ pt: 0, textAlign: "center" }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    mb: 1,
                                                    fontSize: { xs: "1rem", md: "1.08rem" },
                                                    minHeight: 44,
                                                }}
                                            >
                                                {d.DistrictName}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary', mb: 1 }}
                                            >
                                                {d.State}, {d.Country}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: "0.93rem" }}
                                            >
                                                Users: {d.NoOfUsers}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </Grid>
                        ))}
                        {!loading && filtered.length === 0 && (
                            <Grid item xs={12}>
                                <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>
                                        No districts found matching your criteria.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                )}
            </Container>
        </Box>
    );
}
